import { useRef, useCallback } from 'react';
import { CarData } from '../components/CarInfo/CarInfo';

interface UseConsecutiveEventsReturn {
  processEvent: (
    carData: CarData,
    onUpdate: (carData: CarData) => void,
  ) => void;
  reset: () => void;
}

interface UseConsecutiveEventsOptions {
  threshold?: number;
}

export const useConsecutiveEvents = (
  options?: UseConsecutiveEventsOptions,
): UseConsecutiveEventsReturn => {
  const threshold = options?.threshold ?? 2;
  const lastCarDataRef = useRef<CarData | null>(null);
  const consecutiveCountRef = useRef<number>(0);

  const processEvent = useCallback(
    (carData: CarData, onUpdate: (carData: CarData) => void) => {
      // Check if this is the same data as the previous message
      const isSameData =
        lastCarDataRef.current &&
        lastCarDataRef.current.ble_identifier === carData.ble_identifier &&
        lastCarDataRef.current.designation === carData.designation &&
        lastCarDataRef.current.vin === carData.vin;

      if (isSameData) {
        consecutiveCountRef.current += 1;
      } else {
        consecutiveCountRef.current = 1;
        lastCarDataRef.current = carData;
      }

      // Special handling for READY state - always requires 2 consecutive events
      const requiredThreshold = carData.designation === 'READY' ? 2 : threshold;

      // Update state after receiving the required number of consecutive events
      if (consecutiveCountRef.current >= requiredThreshold) {
        onUpdate(carData);
      }
    },
    [threshold],
  );

  const reset = useCallback(() => {
    lastCarDataRef.current = null;
    consecutiveCountRef.current = 0;
  }, []);

  return {
    processEvent,
    reset,
  };
};
